import { Avatar } from "@package/subframe/components/Avatar";
import { Button } from "@package/subframe/components/Button";
import { LinkButton } from "@package/subframe/components/LinkButton";
import { TextField } from "@package/subframe/components/TextField";
import {
  FeatherLock,
  FeatherSearch,
  FeatherWifiOff,
  FeatherX,
} from "@subframe/core";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";

import type { LinkedUserDto } from "@/generated/model/linkedUserDto";
import { useOnlineStatus } from "@/hooks/use-online-status";
import { signInWithCustomToken } from "@/lib/auth";
import { clearDeviceLink, updateCachedUsers } from "@/lib/device/device-link";
import type { OfflineDeviceLink } from "@/lib/offline/db";

import { fetchLinkedUsers } from "./device-setup-api";
import { FirstTimePinSetup } from "./FirstTimePinSetup";
import {
  interpretPinLoginError,
  invalidPinMessage,
  sanitizePin,
  signInWithPin,
  type SignInWithPinResponse,
} from "./pin-login-api";
import {
  filterUsersByQuery,
  initialsFor,
  sortUsersByName,
} from "./pin-login-users";
import { ResetDeviceDialog } from "./ResetDeviceDialog";

const PIN_LENGTH = 4;
const LOCKED_MESSAGE = "Account locked. Ask your manager to reset your PIN.";
const NO_PIN_MESSAGE = "PIN not set — contact your manager";
const OFFLINE_MESSAGE = "Reconnect to log in.";
const GENERIC_ERROR = "Something went wrong. Please try again.";
const NO_USERS_MESSAGE = "No users set up yet";
const NO_MATCHES_MESSAGE = "No one matches that search.";

interface PinLoginShellProps {
  link: OfflineDeviceLink;
  /** Called after the device is reset (returns the login route to setup). */
  onReset: () => void;
}

/** The verified temporary PIN carried into the first-time setup step. */
interface PendingSetup {
  temporaryPin: string;
  wasResetByManager: boolean;
}

/**
 * The PIN-login screen for a linked shared device (NAU-227). Built on the
 * NAU-226 shell: shows the linked nursery name and the cached user list, then
 * lets a user select their name and enter a PIN. Locked accounts show a lock
 * indicator; users with no PIN at all are greyed out until a manager sets a
 * temporary one; a correct temporary PIN routes into first-time setup
 * (NAU-228).
 */
export function PinLoginShell({ link, onReset }: PinLoginShellProps) {
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  const [resetOpen, setResetOpen] = useState(false);

  // cachedUsers is typed `unknown` (JSON payload); guard before treating it as a list.
  // Held in state so a lockout discovered during this session updates the list
  // immediately (the cached isLocked flag is otherwise only as fresh as the last
  // device link / login).
  const [users, setUsers] = useState<LinkedUserDto[]>(() =>
    Array.isArray(link.cachedUsers)
      ? (link.cachedUsers as LinkedUserDto[])
      : [],
  );

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Set when the server verified the user's temporary PIN: the user moves to
  // first-time setup carrying it (the setup endpoint needs it as credential).
  const [pendingSetup, setPendingSetup] = useState<PendingSetup | null>(null);

  // resolveLoginScreen only routes here for a link that has a secret, so this
  // is present in practice. An empty fallback keeps the types honest and fails
  // closed — the server rejects it and the device is sent back to setup.
  const deviceSecret = link.deviceSecret ?? "";

  const selectedUser = users.find((u) => u.userId === selectedUserId) ?? null;

  // Derived rather than sorted into `users` itself, because markUserLocked
  // rewrites that state mid-session and must keep working.
  const visibleUsers = useMemo(
    () => filterUsersByQuery(sortUsersByName(users), searchQuery),
    [users, searchQuery],
  );

  /**
   * Send the device back to setup. The server no longer recognises it, so
   * nothing on this screen can work until a manager links it again.
   */
  const requireRelink = useCallback(async () => {
    await clearDeviceLink();
    onReset();
  }, [onReset]);

  /**
   * Pull the roster from the server. The device's own credential authenticates
   * this, so it works with nobody signed in — which is the point: someone added
   * to the nursery after this device was linked appears here without waiting
   * for a colleague to log in first.
   */
  const refreshUsers = useCallback(async () => {
    try {
      const refreshed = await fetchLinkedUsers(link.deviceUuid, deviceSecret);
      setUsers(refreshed.users);
      await updateCachedUsers(refreshed.users);
    } catch (err) {
      if (interpretPinLoginError(err).kind === "relink") {
        await requireRelink();
        return;
      }
      // Anything else (offline being the common one) leaves the cached roster
      // on screen, which is what makes this screen work without a connection.
    }
  }, [link.deviceUuid, deviceSecret, requireRelink]);

  // On arrival, and again whenever the screen returns to the foreground — a
  // shared tablet sits on this screen for hours between uses, so waking up is
  // the moment its roster is most likely to be out of date.
  useEffect(() => {
    void refreshUsers();

    function onVisibilityChange() {
      if (document.visibilityState === "visible") {
        void refreshUsers();
      }
    }

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [refreshUsers]);

  function selectUser(user: LinkedUserDto) {
    setError(null);
    setPin("");
    setPendingSetup(null);
    setSelectedUserId(user.userId);
  }

  function backToList() {
    setSelectedUserId(null);
    setPin("");
    setError(null);
    setPendingSetup(null);
    // Return to the full roster — the previous query was for a different person.
    setSearchQuery("");
  }

  function onPinChange(value: string) {
    setPin(sanitizePin(value, PIN_LENGTH));
    setError(null);
  }

  /**
   * Mark a user locked for the rest of this session so the lock indicator shows
   * and they can't be re-selected. Deliberately NOT persisted to the cache: a
   * manager clearing the lock server-side would otherwise be overridden by our
   * stale local write until the next refresh. The next roster fetch brings the
   * real state back; the server stays the gate either way.
   */
  function markUserLocked(userId: string) {
    setUsers((prev) =>
      prev.map((u) => (u.userId === userId ? { ...u, isLocked: true } : u)),
    );
  }

  /**
   * Redeem the custom token and enter the app. Shared by the regular login
   * path and first-time PIN setup (which also ends in a session).
   */
  async function completeSession(response: SignInWithPinResponse) {
    await signInWithCustomToken(response.customToken);

    sessionStorage.setItem("session_start_time", Date.now().toString());
    sessionStorage.setItem("login_method", "pin");
    // PIN login is always a nursery-device context, so route to the nursery
    // users' home (matching the _auth layout's redirect for nursery roles).
    navigate({ to: "/checklists", replace: true });
  }

  function handleError(err: unknown, submittedPin: string) {
    setPin("");
    const result = interpretPinLoginError(err);
    switch (result.kind) {
      case "setup":
        // The temporary PIN checked out — carry it into first-time setup.
        setPendingSetup({
          temporaryPin: submittedPin,
          wasResetByManager: result.wasResetByManager,
        });
        return;
      case "no-pin":
        // Stale cache: the user has no PIN at all (not even a temporary one).
        setError(NO_PIN_MESSAGE);
        return;
      case "locked":
        // Reflect the lockout immediately: the user row gets a lock indicator
        // and selecting it shows the lock message instead of a PIN prompt.
        if (selectedUserId) markUserLocked(selectedUserId);
        return;
      case "relink":
        // The credential this device was issued is gone (most likely a manager
        // reset it), so no PIN can work until it is set up again.
        void requireRelink();
        return;
      case "invalid":
        setError(invalidPinMessage(result.attemptsRemaining));
        return;
      default:
        setError(GENERIC_ERROR);
    }
  }

  async function handleSubmit() {
    if (!selectedUser || pin.length !== PIN_LENGTH || !isOnline) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await signInWithPin(
        deviceSecret,
        selectedUser.userId,
        pin,
      );
      await completeSession(response);
    } catch (err) {
      handleError(err, pin);
    } finally {
      setIsSubmitting(false);
    }
  }

  const showFirstTime = selectedUser && pendingSetup;
  const showLocked = selectedUser && !showFirstTime && selectedUser.isLocked;
  const showPinEntry = selectedUser && !showFirstTime && !showLocked;

  return (
    // Fixed to the viewport with the roster scrolling inside it, because the
    // global base styles put `overflow: hidden` on the body — the document
    // itself never scrolls, so a long list has to own its own scroll area or it
    // simply gets clipped. Pinning the header and the "Reset device" footer also
    // keeps the nursery name and the search box on screen while swiping.
    <div className="flex h-dvh w-full flex-col items-center overflow-hidden bg-white px-6 py-12 mobile:py-6">
      <div className="flex min-h-0 w-full max-w-105 flex-1 flex-col items-start gap-8">
        <div className="flex w-full flex-none flex-col items-start gap-2">
          <img
            className="h-8 flex-none object-cover"
            src="/images/logo.svg"
            alt="Logo"
          />
          <span className="text-caption font-caption text-subtext-color">
            Signing in at
          </span>
          <span className="text-heading-1 font-heading-1 text-neutral-900">
            {link.nurseryName}
          </span>
        </div>

        {!selectedUser && (
          <div className="flex min-h-0 w-full flex-1 flex-col items-start gap-3">
            <span className="text-heading-3 font-heading-3 flex-none text-default-font">
              Who&apos;s logging in?
            </span>

            {users.length > 0 && (
              <TextField
                className="h-auto w-full flex-none"
                size="large"
                icon={<FeatherSearch />}
                iconRight={
                  searchQuery ? (
                    <button
                      type="button"
                      aria-label="Clear search"
                      onClick={() => setSearchQuery("")}
                      className="cursor-pointer border-none bg-transparent p-0"
                    >
                      <FeatherX />
                    </button>
                  ) : undefined
                }
              >
                <TextField.Input
                  autoComplete="off"
                  placeholder="Search for your name"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </TextField>
            )}

            {users.length === 0 ? (
              <span className="text-body font-body flex-none text-subtext-color">
                {NO_USERS_MESSAGE}
              </span>
            ) : visibleUsers.length === 0 ? (
              <span className="text-body font-body flex-none text-subtext-color">
                {NO_MATCHES_MESSAGE}
              </span>
            ) : (
              // `overscroll-contain` stops a swipe that hits the end of the list
              // from bouncing the whole screen on iOS.
              <div className="flex min-h-0 w-full flex-1 flex-col items-start gap-1 overflow-y-auto overscroll-contain pb-1">
                {visibleUsers.map((user) => {
                  const noPin = !user.hasPin;
                  const muted = user.isLocked || noPin;
                  return (
                    <button
                      key={user.userId}
                      type="button"
                      disabled={noPin}
                      title={noPin ? NO_PIN_MESSAGE : undefined}
                      onClick={() => selectUser(user)}
                      className="flex w-full flex-none items-center gap-3 rounded-md border border-solid border-secondary-beige-200 bg-transparent px-4 py-3 text-left hover:bg-secondary-beige-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    >
                      <Avatar size="medium">
                        {initialsFor(user.displayName)}
                      </Avatar>
                      <div className="flex grow flex-col items-start">
                        <span
                          className={`text-body-bold font-body-bold ${muted ? "text-subtext-color" : "text-default-font"}`}
                        >
                          {user.displayName}
                        </span>
                        {user.isLocked ? (
                          <span className="text-caption font-caption text-error-700">
                            Account locked
                          </span>
                        ) : noPin ? (
                          <span className="text-caption font-caption text-subtext-color">
                            {NO_PIN_MESSAGE}
                          </span>
                        ) : null}
                      </div>
                      {user.isLocked && (
                        <FeatherLock className="text-subtext-color" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {showLocked && (
          <div className="flex min-h-0 w-full flex-1 flex-col items-start gap-6 overflow-y-auto">
            <div className="flex w-full flex-none flex-col items-start gap-2">
              <FeatherLock className="text-heading-1 text-error-700" />
              <span className="text-heading-2 font-heading-2 text-neutral-900">
                {selectedUser.displayName}
              </span>
              <span className="text-body font-body text-error-700">
                {LOCKED_MESSAGE}
              </span>
            </div>
            <Button
              variant="neutral-secondary"
              size="large"
              onClick={backToList}
            >
              Back
            </Button>
          </div>
        )}

        {showFirstTime && (
          <div className="flex min-h-0 w-full flex-1 flex-col items-start overflow-y-auto">
            <FirstTimePinSetup
              deviceSecret={deviceSecret}
              userId={selectedUser.userId}
              displayName={selectedUser.displayName}
              temporaryPin={pendingSetup.temporaryPin}
              wasResetByManager={pendingSetup.wasResetByManager}
              onBack={backToList}
              onSuccess={completeSession}
            />
          </div>
        )}

        {showPinEntry && (
          <form
            className="flex min-h-0 w-full flex-1 flex-col items-start gap-6 overflow-y-auto"
            onSubmit={(e) => {
              e.preventDefault();
              void handleSubmit();
            }}
          >
            <div className="flex w-full items-center gap-3">
              <Avatar size="medium">
                {initialsFor(selectedUser.displayName)}
              </Avatar>
              <span className="text-heading-3 font-heading-3 text-default-font">
                {selectedUser.displayName}
              </span>
            </div>

            <TextField
              className="h-auto w-full flex-none"
              size="large"
              label="PIN"
              error={!!error}
              helpText={error ?? undefined}
            >
              <TextField.Input
                type="password"
                inputMode="numeric"
                autoComplete="off"
                autoFocus
                placeholder="Enter your 4-digit PIN"
                value={pin}
                disabled={isSubmitting}
                onChange={(e) => onPinChange(e.target.value)}
              />
            </TextField>

            {!isOnline && (
              <div className="flex w-full items-center gap-2 rounded-md bg-error-50 px-4 py-3">
                <FeatherWifiOff className="text-error-700" />
                <span className="text-body font-body text-error-700">
                  {OFFLINE_MESSAGE}
                </span>
              </div>
            )}

            <Button
              className="h-12 w-full flex-none"
              variant="brand-primary"
              size="large"
              type="submit"
              disabled={isSubmitting || !isOnline || pin.length !== PIN_LENGTH}
              loading={isSubmitting}
            >
              Log in
            </Button>

            <LinkButton variant="neutral" size="small" onClick={backToList}>
              Choose a different user
            </LinkButton>
          </form>
        )}
      </div>

      <div className="flex w-full max-w-105 flex-none items-center justify-center pt-8">
        <LinkButton
          variant="neutral"
          size="small"
          onClick={() => setResetOpen(true)}
        >
          Reset device
        </LinkButton>
      </div>

      <ResetDeviceDialog
        deviceUuid={link.deviceUuid}
        open={resetOpen}
        onOpenChange={setResetOpen}
        onReset={() => {
          setResetOpen(false);
          onReset();
        }}
      />
    </div>
  );
}
